import React, {useState} from 'react';

import http from '../../../services/http';
import styles from './styles.module.css';
import {alerta} from '../../../components/Alert';

const MAX_COMENTARIO = 500;
const MAX_FOTO_KB = 5120;

const LEGENDA_NOTA = {
    1: 'Péssimo',
    2: 'Ruim',
    3: 'Regular',
    4: 'Bom',
    5: 'Ótimo',
};

/**
 * Avaliação do sistema pelo admin da banca — mesma coisa que o cambista faz no app,
 * só que aqui quem avalia é quem administra a banca e o alvo é o sistema.
 *
 * A avaliação vai para a Setebit (central), não fica visível para a banca: é canal de
 * sugestão/crítica, não conteúdo do painel. Por isso a tela é só o formulário de envio.
 *
 * Autor e banca saem do token no backend — nada de identidade vai no corpo.
 */
export default function Avaliacao() {

    const [nota, setNota] = useState(0);
    const [notaPreview, setNotaPreview] = useState(0);
    const [comentario, setComentario] = useState('');
    const [foto, setFoto] = useState(null);
    const [previewFoto, setPreviewFoto] = useState(null);
    const [carregaPost, setCarregaPost] = useState(false);

    function limpar() {
        setNota(0);
        setNotaPreview(0);
        setComentario('');
        removerFoto();
    }

    function removerFoto() {
        if (previewFoto) {
            URL.revokeObjectURL(previewFoto);
        }

        setFoto(null);
        setPreviewFoto(null);
    }

    // A foto é opcional (print da tela com problema, na maioria das vezes). Barra aqui
    // o que o backend barraria de qualquer jeito, para não gastar upload à toa.
    function selecionarFoto(evento) {
        const arquivo = evento.target.files[0];

        evento.target.value = '';

        if (!arquivo) {
            return;
        }

        if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(arquivo.type)) {
            alerta({success: false, message: 'A imagem deve ser do tipo: jpg, jpeg, png ou webp!'});
            return;
        }

        if (arquivo.size / 1024 > MAX_FOTO_KB) {
            alerta({success: false, message: 'A imagem excede o tamanho máximo permitido (5 MB)!'});
            return;
        }

        if (previewFoto) {
            URL.revokeObjectURL(previewFoto);
        }

        setFoto(arquivo);
        setPreviewFoto(URL.createObjectURL(arquivo));
    }

    async function enviar() {
        if (nota < 1 || nota > 5) {
            alerta({success: false, message: 'Selecione uma nota de 1 a 5 estrelas!'});
            return;
        }

        try {
            setCarregaPost(true);

            const formData = new FormData();

            formData.append('nota', nota);

            if (comentario.trim()) {
                formData.append('comentario', comentario.trim());
            }

            if (foto) {
                formData.append('foto', foto);
            }

            const response = await http.post('admin/avaliacoes', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            alerta(response.data);

            limpar();
            setCarregaPost(false);
        } catch (e) {
            setCarregaPost(false);
            alerta(e.response.data);
        }
    }

    const notaExibida = notaPreview || nota;

    return (
        <>
            <div className="titulo-principal">Avaliar o Sistema</div>

            <div className="mt-3">
                <div className="bordered" style={{padding: '10px'}}>

                    <p className={styles.ajuda}>
                        Dê uma nota para o sistema e conte o que pode melhorar. Sua avaliação
                        vai direto para a equipe que desenvolve o sistema — use este espaço para
                        sugestões, críticas ou para relatar um problema (pode anexar um print).
                    </p>

                    <div className={styles.estrelas}>
                        {[1, 2, 3, 4, 5].map((valor) => (
                            <button
                                key={valor}
                                type="button"
                                className={`${styles.estrela} ${valor <= notaExibida ? styles.estrelaAtiva : ''}`}
                                title={LEGENDA_NOTA[valor]}
                                disabled={carregaPost}
                                onClick={() => setNota(valor)}
                                onMouseEnter={() => setNotaPreview(valor)}
                                onMouseLeave={() => setNotaPreview(0)}
                            >
                                <i className="fa fa-star"/>
                            </button>
                        ))}

                        <span className={styles.legenda}>
                            {notaExibida ? LEGENDA_NOTA[notaExibida] : 'Toque nas estrelas para dar sua nota'}
                        </span>
                    </div>

                    <div className="form-group">
                        <label>
                            Comentário <small className="text-muted">(opcional — {comentario.length}/{MAX_COMENTARIO})</small>
                        </label>
                        <textarea
                            className="form-control"
                            rows={4}
                            maxLength={MAX_COMENTARIO}
                            value={comentario}
                            disabled={carregaPost}
                            placeholder="Conte o que achou do sistema, o que faltou ou o que poderia melhorar..."
                            onChange={(evento) => setComentario(evento.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Imagem <small className="text-muted">(opcional — jpg, png ou webp, até 5 MB)</small></label>

                        {!previewFoto && (
                            <div>
                                <label className={`btn btn-info ${styles.botaoFoto}`}>
                                    <i className="fa fa-camera mr-2"/> Anexar imagem
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        disabled={carregaPost}
                                        onChange={selecionarFoto}
                                        hidden
                                    />
                                </label>
                            </div>
                        )}

                        {previewFoto && (
                            <div className={styles.previewBox}>
                                <img src={previewFoto} alt="Imagem anexada" className={styles.preview}/>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    disabled={carregaPost}
                                    onClick={removerFoto}
                                >
                                    Remover imagem
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={styles.acoes}>
                        <button
                            className="btn btn-success"
                            onClick={() => enviar()}
                            disabled={carregaPost || nota < 1}
                        >
                            {carregaPost ? 'Enviando...' : 'Enviar avaliação'}
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={() => limpar()}
                            disabled={carregaPost}
                        >
                            Limpar
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
